import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { TableKit } from "@tiptap/extension-table";
import Youtube from "@tiptap/extension-youtube";
import Mathematics from "@tiptap/extension-mathematics";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading2, Heading3, List, ListOrdered, Quote, Minus,
  Image as ImageIcon, Undo2, Redo2, AlignLeft, AlignCenter, AlignRight,
  Loader2, Scissors, RemoveFormatting, PanelTop, AlertTriangle,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Callout, Figure, GlossaryHighlight, HeadingWithId, PreviewBreak, SiteLink, glossaryKey,
} from "./editor/extensions";
import {
  ColorTool, GlossaryTool, HighlightTool, InternalLinkTool, LinkTool, MathTool, TableBar, TableInsertBtn,
  ToolbarBtn, VideoTool,
} from "./editor/tools";

export { PREVIEW_BREAK_RE } from "./editor/extensions";
// Début du texte identique au titre ou à la description (texte collé tel quel depuis un document) :
// comparaison sans accents, majuscules ni ponctuation ; un long texte peut aussi n'être que le début de l'autre.
const normText = (t) => (t || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
function sameText(a, b) {
  const x = normText(a);
  const y = normText(b);
  if (!x || !y) return false;
  if (x === y) return true;
  return Math.min(x.length, y.length) >= 30 && (x.startsWith(y) || y.startsWith(x));
}
/** Premiers blocs du texte qui répètent le titre ou la description : [{ from, to, label }]. */
function findLeadDuplicates(doc, leads) {
  const found = [];
  const used = new Set();
  let pos = 0;
  for (let i = 0; i < Math.min(doc.childCount, 3); i += 1) {
    const node = doc.child(i);
    const text = node.textContent.trim();
    if (!text) { pos += node.nodeSize; continue; } // ligne vide entre les deux : on continue
    const lead = leads.find((l, k) => !used.has(k) && sameText(text, l.text));
    if (!lead) break;
    used.add(leads.indexOf(lead));
    found.push({ from: pos, to: pos + node.nodeSize, label: lead.label });
    pos += node.nodeSize;
  }
  return found;
}

// Texte collé depuis Google Docs, Word, Notion… : on garde la structure (titres, gras, italique,
// listes, liens, tableaux) mais on retire la taille, la police et les couleurs imposées par le document
// d'origine. Sinon le texte collé reste en noir sur fond sombre et les titres semblent ne rien faire.
const PASTE_STRIP_STYLES = ["font-size", "font-family", "color", "background-color", "background", "line-height"];
function cleanPastedHTML(html) {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("[style]").forEach((el) => {
      PASTE_STRIP_STYLES.forEach((prop) => el.style.removeProperty(prop));
      if (!el.getAttribute("style")?.trim()) el.removeAttribute("style");
    });
    doc.querySelectorAll("font").forEach((el) => el.replaceWith(...el.childNodes));
    // Niveaux de titres : Titre 1 → intertitre (H2), Titre 4 à 6 → sous-titre (H3)
    doc.querySelectorAll("h1, h4, h5, h6").forEach((el) => {
      const h = doc.createElement(el.tagName === "H1" ? "h2" : "h3");
      [...el.attributes].forEach((a) => h.setAttribute(a.name, a.value));
      h.append(...el.childNodes);
      el.replaceWith(h);
    });
    doc.querySelectorAll("span:not([style]):not([class]):not([data-type])").forEach((el) => el.replaceWith(...el.childNodes));
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

const Sep = () => <div className="w-px h-5 bg-white/10 mx-1" />;

/**
 * Éditeur de texte des cours et des analyses.
 * - accent : "orange" (cours) ou "violet" (analyses) pour la couleur des encadrés dans l'éditeur.
 * - glossaryTerms : définitions du glossaire [{ id, slug, title }] ; si fourni, affiche le bouton « Glossaire »
 *   et souligne les mots détectés automatiquement (sauf ceux de glossaryOff).
 * - internalTargets : contenus du site proposés par le bouton « Lien interne »
 *   [{ key: "course:id" | "article:id", title, href, kind, sub? }].
 * - leads : [{ label: "le titre", text }, { label: "la description", text }] ; si le texte commence par l'un d'eux,
 *   un bandeau propose de le retirer (il apparaîtrait deux fois sur la page de lecture).
 * - onNormalize(html) : appelé quand l'éditeur ajoute un identifiant aux titres d'un ancien texte
 *   (nécessaire au sommaire) ; à enregistrer dans le formulaire sans le marquer comme modifié.
 */
export default function RichTextEditor({
  value, onChange, placeholder = "Écrivez ici…", stickyTop = 0, minHeight = "320px", enablePreviewBreak = false,
  accent = "orange", glossaryTerms = null, glossaryOff = null, internalTargets = null, onNormalize, leads = null,
}) {
  const glossaryRef = useRef({ terms: [], off: [] });
  glossaryRef.current = { terms: glossaryTerms || [], off: glossaryOff || [] };
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mathEdit, setMathEdit] = useState(null); // formule cliquée dans le texte
  const isProgrammatic = useRef(false);
  const [dupes, setDupes] = useState([]); // début du texte identique au titre / à la description
  const [dupesDismissed, setDupesDismissed] = useState(false);
  const editor = useEditor({
    // TipTap v3 ne rafraîchit plus la barre d'outils quand la sélection change :
    // sans cette option, les boutons (gras, titre…) n'indiquent pas l'état du texte sélectionné.
    shouldRerenderOnTransaction: true,
    extensions: [
      // Titres, lien et souligné sont ajoutés plus bas avec leurs réglages : on les retire du StarterKit
      StarterKit.configure({ heading: false, link: false, underline: false }),
      HeadingWithId.configure({ levels: [1, 2, 3] }),
      Underline,
      SiteLink.configure({ openOnClick: false, HTMLAttributes: { class: "text-emerald-400 underline" } }),
      Callout,
      Figure,
      GlossaryHighlight.configure({ getGlossary: () => glossaryRef.current }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TableKit.configure({ table: { resizable: false } }),
      Youtube.configure({ nocookie: true, controls: true, width: 640, height: 360, HTMLAttributes: { class: "video-embed" } }),
      Mathematics.configure({
        blockOptions: { onClick: (node, pos) => setMathEdit({ type: "block", pos, latex: node.attrs.latex }) },
        inlineOptions: { onClick: (node, pos) => setMathEdit({ type: "inline", pos, latex: node.attrs.latex }) },
        katexOptions: { throwOnError: false },
      }),
      PreviewBreak,
    ],
    content: value || "",
    onUpdate: ({ editor: ed, transaction }) => {
      // Seules les vraies modifications du texte comptent (pas le rafraîchissement du soulignement du glossaire)
      if (isProgrammatic.current || !transaction.docChanged) return;
      onChange?.(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose prose-invert prose-emerald max-w-none focus:outline-none px-5 py-4 text-zinc-200",
        style: `min-height: ${minHeight}`,
        "data-testid": "rich-editor-content",
      },
      transformPastedHTML: cleanPastedHTML,
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items || [];
        for (const item of items) {
          if (item.type.startsWith("image/")) {
            event.preventDefault();
            uploadAndInsert(item.getAsFile());
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event, slice, moved) => {
        if (moved) return false; // déplacement d'un élément déjà dans le texte
        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) return false;
        const imgs = Array.from(files).filter((f) => f.type.startsWith("image/"));
        if (imgs.length === 0) return false;
        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
        imgs.forEach((f) => uploadAndInsert(f, pos?.pos));
        return true;
      },
    },
  });
  const uploadAndInsert = useCallback(
    async (file, atPos) => {
      if (!editor || !file) return;
      try {
        setUploading(true);
        const form = new FormData();
        form.append("file", file);
        const res = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
        const url = res.data?.url;
        if (!url) throw new Error("URL manquante");
        const node = { type: "figure", attrs: { src: url } };
        if (atPos != null) editor.chain().focus().insertContentAt(atPos, node).run();
        else editor.chain().focus().insertContent(node).run();
      } catch (e) {
        toast.error("Upload échoué : " + (e?.response?.data?.detail || e.message));
      } finally {
        setUploading(false);
      }
    },
    [editor]
  );
  // Titres d'un ancien texte : un identifiant (ancres du sommaire) et H1 → H2 (le titre de la page est le seul H1)
  const normalizeHeadings = useCallback(() => {
    if (!editor || editor.isDestroyed) return;
    const tr = editor.state.tr;
    let changed = false;
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name === "heading" && (!node.attrs.id || node.attrs.level === 1)) {
        tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          id: node.attrs.id || `t-${Math.random().toString(36).slice(2, 8)}`,
          level: node.attrs.level === 1 ? 2 : node.attrs.level,
        });
        changed = true;
      }
    });
    if (!changed) return;
    isProgrammatic.current = true;
    editor.view.dispatch(tr.setMeta("addToHistory", false));
    isProgrammatic.current = false;
    onNormalize?.(editor.getHTML());
  }, [editor, onNormalize]);
  // Mots du glossaire : redessiner le soulignement quand la liste des définitions ou des mots décochés change
  const glossaryKeyStr = `${(glossaryTerms || []).length}|${(glossaryOff || []).join(",")}`;
  useEffect(() => {
    if (editor && !editor.isDestroyed) editor.view.dispatch(editor.state.tr.setMeta(glossaryKey, true).setMeta("addToHistory", false));
  }, [editor, glossaryKeyStr]);
  // Garder l'éditeur à jour quand le texte change de l'extérieur (chargement, brouillon récupéré…)
  // (en différé : les images sont dessinées par React, qui ne peut pas le faire pendant un autre rendu)
  useEffect(() => {
    if (!editor) return;
    queueMicrotask(() => {
      if (editor.isDestroyed) return;
      if ((value || "") !== editor.getHTML()) {
        isProgrammatic.current = true;
        editor.commands.setContent(value || "", { emitUpdate: false });
        isProgrammatic.current = false;
      }
      normalizeHeadings();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);
  // Titre ou description répétés au début du texte : recherche à chaque modification
  const leadsKey = JSON.stringify((leads || []).map((l) => l.text || ""));
  useEffect(() => {
    if (!editor || !leads) return undefined;
    const check = () => {
      if (editor.isDestroyed) return;
      setDupes(findLeadDuplicates(editor.state.doc, leads.filter((l) => (l.text || "").trim())));
    };
    // « transaction » et non « update » : le chargement du texte (setContent) doit aussi être vérifié
    const onTr = ({ transaction }) => transaction.docChanged && check();
    check();
    editor.on("transaction", onTr);
    return () => editor.off("transaction", onTr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, leadsKey]);
  if (!editor) return null;
  const removeDupes = () => {
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        [...dupes].reverse().forEach((d) => tr.delete(d.from, d.to));
        return true;
      })
      .run();
    toast.success("Retiré du texte");
  };
  const dupeLabel = [...new Set(dupes.map((d) => d.label))].join(" et ");
  const onFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadAndInsert(file);
    e.target.value = "";
  };
  const insertPreviewBreak = () => {
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        const found = [];
        tr.doc.descendants((node, pos) => {
          if (node.type.name === "previewBreak") found.push({ pos, size: node.nodeSize });
        });
        found.reverse().forEach(({ pos, size }) => tr.delete(pos, pos + size));
        return true;
      })
      .insertContent({ type: "previewBreak" })
      .run();
  };
  const setBlock = (level) => {
    const chain = editor.chain().focus();
    (level ? chain.toggleHeading({ level }) : chain.setParagraph()).run();
  };
  // Efface toute la mise en forme de la sélection (gras, couleurs, titres, listes…)
  const clearFormatting = () => editor.chain().focus().unsetAllMarks().clearNodes().run();
  return (
    <div className={`rounded-lg border border-white/10 bg-white/[0.02] ${accent === "violet" ? "editor-violet" : ""}`}>
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFileInput} />
      <div className="sticky z-30 rounded-t-lg bg-[#0F0F10]/95 backdrop-blur shadow-[0_6px_12px_-6px_rgba(0,0,0,0.6)]" style={{ top: stickyTop }}>
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-white/5" data-testid="rich-editor-toolbar">
          <ToolbarBtn testid="rt-p" active={editor.isActive("paragraph") && !editor.isActive("heading")} onClick={() => setBlock(null)} title="Paragraphe">¶</ToolbarBtn>
          <ToolbarBtn testid="rt-h2" active={editor.isActive("heading", { level: 2 })} onClick={() => setBlock(2)} title="Titre 2 (intertitre : apparaît dans le sommaire)"><Heading2 className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-h3" active={editor.isActive("heading", { level: 3 })} onClick={() => setBlock(3)} title="Titre 3 (sous-titre)"><Heading3 className="w-4 h-4" /></ToolbarBtn>
          <Sep />
          <ToolbarBtn testid="rt-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras"><Bold className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique"><Italic className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Souligné"><UnderlineIcon className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-strike" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barré"><Strikethrough className="w-4 h-4" /></ToolbarBtn>
          <ColorTool editor={editor} />
          <HighlightTool editor={editor} />
          <ToolbarBtn testid="rt-clear" onClick={clearFormatting} title="Effacer la mise en forme"><RemoveFormatting className="w-4 h-4" /></ToolbarBtn>
          <Sep />
          <ToolbarBtn testid="rt-align-left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Aligner à gauche"><AlignLeft className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-align-center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Centrer"><AlignCenter className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-align-right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Aligner à droite"><AlignRight className="w-4 h-4" /></ToolbarBtn>
          <Sep />
          <ToolbarBtn testid="rt-ul" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces"><List className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-ol" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citation"><Quote className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-callout" active={editor.isActive("callout")} onClick={() => editor.chain().focus().toggleWrap("callout").run()} title="Encadré (sélectionnez un ou plusieurs paragraphes ; recliquer pour retirer)"><PanelTop className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Séparateur"><Minus className="w-4 h-4" /></ToolbarBtn>
          <Sep />
          <LinkTool editor={editor} />
          {internalTargets && <InternalLinkTool editor={editor} targets={internalTargets} />}
          {glossaryTerms && <GlossaryTool editor={editor} terms={glossaryTerms} />}
          <Sep />
          <ToolbarBtn testid="rt-image" disabled={uploading} onClick={() => fileInputRef.current?.click()} title="Insérer une image">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
          </ToolbarBtn>
          <VideoTool editor={editor} />
          <TableInsertBtn editor={editor} />
          <MathTool editor={editor} editing={mathEdit} setEditing={setMathEdit} />
          <div className="flex-1" />
          {enablePreviewBreak && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={insertPreviewBreak}
              title="Placer la fin de l'aperçu gratuit au curseur"
              data-testid="rt-preview-break"
              className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded text-xs font-medium bg-violet-500/15 text-violet-300 border border-violet-500/40 hover:bg-violet-500/25 transition-colors mr-1"
            >
              <Scissors className="w-3.5 h-3.5" /> Fin d'aperçu
            </button>
          )}
          <ToolbarBtn testid="rt-undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} title="Annuler"><Undo2 className="w-4 h-4" /></ToolbarBtn>
          <ToolbarBtn testid="rt-redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} title="Rétablir"><Redo2 className="w-4 h-4" /></ToolbarBtn>
        </div>
        <TableBar editor={editor} />
      </div>
      {dupes.length > 0 && !dupesDismissed && (
        <div className="mx-4 mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3 text-sm flex flex-wrap items-center gap-3" data-testid="rt-duplicate-lead">
          <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />
          <span className="flex-1 min-w-[240px] text-zinc-300">
            Le texte commence par <b>{dupeLabel}</b>, déjà affiché{dupes.length > 1 ? "s" : ""} en haut de la page : {dupes.length > 1 ? "ils apparaîtraient" : "il apparaîtrait"} deux fois.
          </span>
          <button type="button" onClick={removeDupes} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-500 text-black text-xs font-medium hover:bg-emerald-300" data-testid="rt-duplicate-remove">
            Retirer du texte
          </button>
          <button type="button" onClick={() => setDupesDismissed(true)} className="h-8 px-3 rounded-md text-xs text-zinc-400 hover:text-white" data-testid="rt-duplicate-ignore">
            Ignorer
          </button>
        </div>
      )}
      <EditorContent editor={editor} />
      <div className="px-4 py-1.5 border-t border-white/5 text-[10px] text-zinc-500 bg-black/20 rounded-b-lg">
        Astuce : glissez-déposez ou collez une image ; cliquez dessus pour la redimensionner, l'aligner et ajouter une légende. Cliquez sur une formule pour la modifier.
        {glossaryTerms && " Les mots soulignés en pointillés orange sont liés au glossaire."}
        {enablePreviewBreak && " Le bouton « Fin d'aperçu » place la coupure à l'endroit du curseur (déplaçable par glisser-déposer)."}
      </div>
    </div>
  );
}
